/**
 * QUIÉN ADMINISTRA ESTE CLUB.
 *
 * QUÉ PROBLEMA RESUELVE. Un club se veía como una entidad sin gente detrás:
 * quien tenía una categoría mal puesta, una inscripción sin aprobar o una
 * queja no tenía a quién escribirle. Javier: «hay que poner quiénes son los
 * administradores del club que todos puedan ver y contactarlos si es
 * necesario».
 *
 * LO VE CUALQUIERA QUE PUEDA VER EL CLUB, y eso es a propósito: administrar
 * un club es un cargo, no un dato personal. Quien lo acepta responde por lo
 * que ahí pasa, y para eso hay que saber quién es.
 *
 * SE CONTACTA POR LA FICHA y no con un teléfono puesto acá. La ficha de cada
 * jugador ya es el único lugar donde vive la regla de a quién se le muestra
 * qué; publicar el número personal de alguien en esta lista sería una segunda
 * regla, más suelta, decidida en otro lado.
 *
 * SI FALLA, NO DICE NADA. Es un dato de apoyo al costado de la pantalla del
 * club: una alerta roja acá haría ver el club entero como roto por algo que
 * no impide hacer nada.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { CommunityAdmin } from '@tennisup/contracts';

import { Avatar } from './Avatar';
import { api, assetUrl } from '../lib/api';

export function CommunityAdmins({ communityId }: { communityId: string }): JSX.Element | null {
  const { t } = useTranslation();
  const [admins, setAdmins] = useState<CommunityAdmin[] | null>(null);

  useEffect(() => {
    let vivo = true;
    api
      .listCommunityAdmins(communityId)
      .then((lista) => {
        // `Array.isArray` y no `?? []`: un tipo es una promesa sobre el
        // contrato, no una garantía sobre lo que llegó por el cable, y acá un
        // `.map` sobre otra cosa tumbaría la pantalla del club entera.
        if (vivo) setAdmins(Array.isArray(lista) ? lista : []);
      })
      .catch(() => {
        if (vivo) setAdmins([]);
      });
    return () => {
      vivo = false;
    };
  }, [communityId]);

  // Sin nadie que mostrar no se dibuja el título: un encabezado seguido de
  // nada se lee como que algo no cargó.
  if (admins === null || admins.length === 0) return null;

  return (
    <section className="club-admins">
      <h2 className="section-label">{t('communities.admins')}</h2>
      <p className="help">{t('communities.adminsHelp')}</p>

      <ul className="club-admin-list">
        {admins.map((admin) => (
          <li key={admin.userId}>
            <Link className="club-admin" to={`/jugadores/${admin.userId}`}>
              <Avatar url={assetUrl(admin.avatarPath)} name={admin.displayName} size={36} />
              <span className="club-admin-who">
                <span className="club-admin-name">{admin.displayName}</span>
                {/* QUIÉN LO FUNDÓ, dicho: no es lo mismo escribirle a quien
                    creó el club que a quien lo ayuda a llevarlo, y quien
                    busca a alguien para un reclamo quiere saber la
                    diferencia. */}
                {admin.isFounder && <span className="help">{t('communities.founder')}</span>}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
